import { useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { trackEvent } from "@/lib/posthog";
import { AppLayout } from "@/components/layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useListPackages,
  useGetMyCredits,
  usePurchasePackage,
  useUpdateAutoRenew,
  getGetMyCreditsQueryKey,
} from "@workspace/api-client-react";
import {
  Package,
  CreditCard,
  Sparkles,
  RefreshCw,
  AlertTriangle,
  CalendarClock,
  Clock,
  CheckCircle2,
  CalendarX2,
  Zap,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";

function getDaysUntil(isoDate: string | null): number | null {
  if (!isoDate) return null;
  const diff = new Date(isoDate).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function ExpiryBadge({ expiresAt }: { expiresAt: string | null }) {
  if (!expiresAt) return null;
  const days = getDaysUntil(expiresAt);
  if (days === null) return null;

  if (days < 0) {
    return (
      <Badge variant="destructive" className="text-xs gap-1">
        <CalendarX2 className="w-3 h-3" /> Expired
      </Badge>
    );
  }
  if (days <= 3) {
    return (
      <Badge className="text-xs gap-1 bg-red-500/20 text-red-400 border-red-500/30">
        <Clock className="w-3 h-3" /> {days}d left
      </Badge>
    );
  }
  if (days <= 7) {
    return (
      <Badge className="text-xs gap-1 bg-orange-500/20 text-orange-400 border-orange-500/30">
        <CalendarClock className="w-3 h-3" /> {days}d left
      </Badge>
    );
  }
  if (days <= 14) {
    return (
      <Badge className="text-xs gap-1 bg-amber-500/20 text-amber-400 border-amber-500/30">
        <CalendarClock className="w-3 h-3" /> {days}d left
      </Badge>
    );
  }
  return (
    <Badge className="text-xs gap-1 bg-white/10 text-muted-foreground border-white/10">
      <CalendarClock className="w-3 h-3" />
      {days}d left
    </Badge>
  );
}

function expiryBarColor(expiresAt: string | null): string {
  if (!expiresAt) return "";
  const days = getDaysUntil(expiresAt);
  if (days === null || days < 0) return "bg-red-500/40";
  if (days <= 3) return "bg-red-500";
  if (days <= 7) return "bg-orange-500";
  if (days <= 14) return "bg-amber-500";
  return "";
}

export default function Packages() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const gymId = user?.gymId || "";
  const { data: packages, isLoading: pkgLoading } = useListPackages(gymId);
  const { data: credits, isLoading: creditsLoading } = useGetMyCredits();
  const purchaseMutation = usePurchasePackage();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    purchaseId: string;
    packageName: string;
    enabling: boolean;
  }>({ open: false, purchaseId: "", packageName: "", enabling: false });

  const autoRenewMutation = useUpdateAutoRenew({
    mutation: {
      onSuccess: (_data: any, variables: any) => {
        queryClient.invalidateQueries({ queryKey: getGetMyCreditsQueryKey() });
        toast({
          title: variables.data.autoRenew ? "Auto-Renewal Enabled" : "Auto-Renewal Disabled",
          description: variables.data.autoRenew
            ? "Your package will automatically renew when credits run low."
            : "Your package will no longer auto-renew.",
        });
      },
      onError: (err: Error) => {
        toast({ title: "Error", description: err.message || "Could not update auto-renewal", variant: "destructive" });
      },
    },
  });

  const handleToggleAutoRenew = (purchaseId: string, packageName: string, currentAutoRenew: boolean) => {
    setConfirmDialog({ open: true, purchaseId, packageName, enabling: !currentAutoRenew });
  };

  const handleConfirmAutoRenew = () => {
    autoRenewMutation.mutate({ id: confirmDialog.purchaseId, data: { autoRenew: confirmDialog.enabling } });
    setConfirmDialog({ open: false, purchaseId: "", packageName: "", enabling: false });
  };

  const handlePurchase = async (packageId: string) => {
    try {
      const result: any = await purchaseMutation.mutateAsync({ data: { packageId, gymId } });
      trackEvent("package_purchased", { packageId, gymId });
      const redirectUrl = result?.checkoutUrl || result?.url;
      if (redirectUrl) { window.location.href = redirectUrl; return; }
      toast({ title: "Package Purchased!", description: "Your credits have been added." });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Could not purchase";
      toast({ title: "Error", description: message, variant: "destructive" });
    }
  };

  const totalCredits = (credits || []).reduce((sum: number, c: any) => sum + (c.creditsRemaining || 0), 0);
  const activeCredits = (credits || []).filter((c: any) => c.creditsRemaining > 0);
  const exhausted = (credits || []).filter((c: any) => c.creditsRemaining === 0);

  const defaultTab = credits && credits.length > 0 ? "mine" : "available";

  return (
    <AppLayout>
      <div className="flex flex-col gap-6 pb-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-4xl font-display font-bold text-white mb-2">Session Packages</h1>
            <p className="text-muted-foreground text-lg">Save more with bulk session credits</p>
          </div>
          <Card className="glass-panel border-primary/30 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Available Credits</p>
                <p className="text-3xl font-mono font-bold text-primary">{totalCredits}</p>
              </div>
            </div>
          </Card>
        </div>

        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="bg-white/5 border border-white/10 h-10 mb-2">
            <TabsTrigger value="mine" className="data-[state=active]:bg-primary data-[state=active]:text-black font-semibold px-5">
              My Packages
              {credits && credits.length > 0 && (
                <span className="ml-2 bg-white/10 data-[state=active]:bg-black/20 text-xs rounded-full px-2 py-0.5">
                  {credits.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="available" className="data-[state=active]:bg-primary data-[state=active]:text-black font-semibold px-5">
              Available Packages
            </TabsTrigger>
          </TabsList>

          {/* ── MY PACKAGES TAB ── */}
          <TabsContent value="mine" className="space-y-4 mt-4">
            {creditsLoading ? (
              <div className="space-y-4">
                {[1, 2].map(i => <div key={i} className="h-40 bg-white/5 rounded-2xl animate-pulse" />)}
              </div>
            ) : !credits || credits.length === 0 ? (
              <Card className="glass-panel border-white/5 p-10 text-center">
                <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-40" />
                <p className="text-white font-semibold text-lg mb-2">No packages yet</p>
                <p className="text-muted-foreground text-sm mb-6">Purchase a session package to get started with bulk bookings.</p>
                <Button className="bg-primary text-black font-bold" onClick={() => {
                  const el = document.querySelector('[data-state]') as any;
                  el?.click();
                }}>Browse Available Packages</Button>
              </Card>
            ) : (
              <>
                {activeCredits.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold px-1">Active</p>
                    {activeCredits.map((c: any) => {
                      const hasFailure = c.autoRenewFailureCount > 0 && !c.autoRenew;
                      const isAutoRenewing = !!c.autoRenew;
                      const threshold = c.lowCreditThreshold ?? 1;
                      const isLow = c.creditsRemaining <= threshold && c.creditsRemaining > 0;
                      const pct = c.creditsTotal > 0 ? (c.creditsRemaining / c.creditsTotal) * 100 : 0;
                      const days = getDaysUntil(c.expiresAt);
                      const expColor = expiryBarColor(c.expiresAt);
                      const creditBarColor = isLow ? "bg-amber-500" : "bg-primary";
                      const barColor = expColor || creditBarColor;

                      return (
                        <Card key={c.id} className={`glass-panel p-5 ${isLow ? "border-amber-500/30" : "border-white/5"}`}>
                          {/* Top row */}
                          <div className="flex items-start justify-between gap-4 mb-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <p className="font-bold text-white truncate">{c.packageName}</p>
                                {isAutoRenewing && (
                                  <Badge variant="outline" className="border-primary/40 text-primary text-xs gap-1 shrink-0">
                                    <RefreshCw className="w-3 h-3" /> Auto-Renew
                                  </Badge>
                                )}
                                {hasFailure && (
                                  <Badge variant="destructive" className="text-xs gap-1 shrink-0">
                                    <AlertTriangle className="w-3 h-3" /> Renewal Failed
                                  </Badge>
                                )}
                                {isLow && (
                                  <Badge className="text-xs gap-1 bg-amber-500/20 text-amber-400 border-amber-500/30 shrink-0">
                                    <AlertTriangle className="w-3 h-3" /> Running Low
                                  </Badge>
                                )}
                                <ExpiryBadge expiresAt={c.expiresAt} />
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {c.creditsUsed} of {c.creditsTotal} sessions used
                                {c.expiresAt && (
                                  <span className="ml-2 text-white/40">
                                    · Expires {new Date(c.expiresAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                                  </span>
                                )}
                              </p>
                            </div>

                            {/* Credits counter */}
                            <div className="text-right shrink-0">
                              <p className={`text-3xl font-mono font-bold ${isLow ? "text-amber-400" : "text-primary"}`}>
                                {c.creditsRemaining}
                              </p>
                              <p className="text-xs text-muted-foreground">credits left</p>
                            </div>
                          </div>

                          {/* Progress bar */}
                          <div className="w-full bg-white/5 h-2.5 rounded-full overflow-hidden mb-4">
                            <div
                              className={`h-full rounded-full transition-all ${barColor}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>

                          {/* Bottom row */}
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                              <label htmlFor={`ar-${c.id}`} className="text-xs text-muted-foreground cursor-pointer select-none">
                                Auto-renew
                              </label>
                              <Switch
                                id={`ar-${c.id}`}
                                checked={isAutoRenewing}
                                disabled={autoRenewMutation.isPending}
                                onCheckedChange={() => handleToggleAutoRenew(c.id, c.packageName, isAutoRenewing)}
                              />
                            </div>

                            <Button
                              size="sm"
                              className="bg-primary text-black font-bold gap-1.5"
                              onClick={() => navigate("/book")}
                            >
                              <Zap className="w-3.5 h-3.5" /> Book a Session
                            </Button>
                          </div>

                          {/* Expiry urgency message */}
                          {days !== null && days >= 0 && days <= 7 && (
                            <p className="text-xs text-orange-400/90 mt-3 pt-3 border-t border-white/5">
                              {days === 0
                                ? "This package expires today — book before midnight!"
                                : `Only ${days} day${days === 1 ? "" : "s"} left to use ${c.creditsRemaining} credit${c.creditsRemaining === 1 ? "" : "s"}. Book your sessions soon!`}
                            </p>
                          )}
                          {isLow && !isAutoRenewing && (
                            <p className="text-xs text-amber-400/80 mt-2">
                              Only {c.creditsRemaining} credit{c.creditsRemaining === 1 ? "" : "s"} left. Consider purchasing more soon.
                            </p>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                )}

                {exhausted.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold px-1">Exhausted</p>
                    {exhausted.map((c: any) => (
                      <Card key={c.id} className="glass-panel border-white/5 p-5 opacity-60">
                        <div className="flex items-center justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <CheckCircle2 className="w-4 h-4 text-muted-foreground" />
                              <p className="font-semibold text-muted-foreground">{c.packageName}</p>
                              <Badge variant="outline" className="text-xs border-white/10 text-muted-foreground">All Used</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">{c.creditsTotal} sessions · Purchased {new Date(c.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-mono font-bold text-muted-foreground">0</p>
                            <p className="text-xs text-muted-foreground">credits left</p>
                          </div>
                        </div>
                        <div className="w-full bg-white/5 h-1.5 rounded-full mt-3" />
                      </Card>
                    ))}
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* ── AVAILABLE PACKAGES TAB ── */}
          <TabsContent value="available" className="mt-4">
            {pkgLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => <div key={i} className="h-64 bg-white/5 rounded-2xl animate-pulse" />)}
              </div>
            ) : !packages || packages.length === 0 ? (
              <Card className="glass-panel border-white/5 p-10 text-center">
                <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-40" />
                <p className="text-white font-semibold text-lg">No packages available</p>
                <p className="text-muted-foreground text-sm mt-2">Your gym hasn't set up session packages yet.</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {(packages || []).map((pkg: any, i: number) => (
                  <Card
                    key={pkg.id}
                    className={`glass-panel border overflow-hidden transition-all hover:-translate-y-1 ${i === 1 ? "border-primary shadow-lg shadow-primary/10" : "border-white/5"}`}
                  >
                    {i === 1 && <div className="bg-primary text-black text-center text-xs font-bold py-1.5 uppercase tracking-wider">Most Popular</div>}
                    <CardContent className="p-6 flex flex-col h-full">
                      <div className="flex items-center gap-2 mb-2">
                        <Package className="w-5 h-5 text-primary" />
                        <h3 className="text-xl font-bold text-white">{pkg.name}</h3>
                      </div>
                      {pkg.description && <p className="text-sm text-muted-foreground mb-6">{pkg.description}</p>}
                      <div className="mb-6">
                        <p className="text-4xl font-mono font-bold text-white">${pkg.priceTotal}</p>
                        <p className="text-sm text-muted-foreground mt-1">${pkg.pricePerSession}/session</p>
                      </div>
                      <div className="space-y-2 mb-6 flex-1">
                        <div className="flex items-center gap-2 text-sm">
                          <Sparkles className="w-4 h-4 text-primary" />
                          <span className="text-white">{pkg.sessionCount} sessions</span>
                        </div>
                        {pkg.savingsPct && (
                          <div className="flex items-center gap-2 text-sm">
                            <Sparkles className="w-4 h-4 text-green-400" />
                            <span className="text-green-400">Save {pkg.savingsPct}%</span>
                          </div>
                        )}
                        {pkg.expiryDays && (
                          <div className="flex items-center gap-2 text-sm">
                            <CalendarClock className="w-4 h-4 text-muted-foreground" />
                            <span className="text-muted-foreground">Valid for {pkg.expiryDays} days</span>
                          </div>
                        )}
                      </div>
                      <Button
                        onClick={() => handlePurchase(pkg.id)}
                        disabled={purchaseMutation.isPending}
                        className={`w-full h-12 rounded-xl font-bold ${i === 1 ? "bg-primary text-black" : "bg-white/5 text-white hover:bg-white/10 border border-white/10"}`}
                      >
                        Purchase
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <AlertDialog
        open={confirmDialog.open}
        onOpenChange={(open) => {
          if (!open) setConfirmDialog({ open: false, purchaseId: "", packageName: "", enabling: false });
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDialog.enabling ? "Enable Auto-Renewal?" : "Disable Auto-Renewal?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.enabling
                ? `Your "${confirmDialog.packageName}" package will automatically renew when your credits run low. You'll be charged using your payment method on file.`
                : `Auto-renewal will be turned off for "${confirmDialog.packageName}". You'll need to manually purchase a new package when your credits run out.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmAutoRenew}>
              {confirmDialog.enabling ? "Enable Auto-Renewal" : "Disable Auto-Renewal"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
